export default customElements.define('w-progress-tracker', class ProgressTracker extends wealthify.CustomElementBaseClass {

    constructor() {
        const styleSelector = 'c-progress-tracker';
        const config = {
            styleSelector,
            templateHTML: `
            <div data-root>
                <div class="${styleSelector}__step-elements" aria-hidden="true" data-stepelements></div>
                <div role="region" aria-live="polite">
                    <span class="u-display__hidden" data-arialabel></span>
                    <p class="${styleSelector}__label" data-label></p>
                    <p class="${styleSelector}__description" data-description></p>
                <div>
                <template data-steptemplate>
                    <div class="${styleSelector}__step"></div>
                </template>
            </div>`,
            cacheDom: [ 'data-stepelements', 'data-arialabel', 'data-label', 'data-description', 'data-steptemplate' ]
        };

        super(config);

        this.onConnectedCallback = () => {
            this.classList.add(styleSelector);
        };
    }

    static get requiredProperties () {
        return ['label', 'description'];
    }

    static get observedProps () {
        return [
            new wealthify.Prop('steps', {
                type: wealthify.propTypes.Array(),
                validators: [wealthify.propValidators.ArrayObjectContainsKeys(this.requiredProperties), wealthify.propValidators.ArrayHasMinimumLength(2)],
            }),
            new wealthify.Prop('current', {
                type: wealthify.propTypes.Number(),
                default: 0
            }),
            new wealthify.Prop('initialstate', {
                type: wealthify.propTypes.Object(),
                default: {
                    'label': null,
                    'description': null,
                },
                validators: [wealthify.propValidators.ObjectContainsKeys(this.requiredProperties)]
            }),
            new wealthify.Prop('completestate', {
                type: wealthify.propTypes.Object(),
                default: {
                    'label': null,
                    'description': null,
                },
                validators: [wealthify.propValidators.ObjectContainsKeys(this.requiredProperties)]
            }),
            new wealthify.Prop('label', {required: true,}),
        ];
    }

    get propCallbacks() {
        return {
            steps: () => {
                this._createStepElements();
                this._updateState();
            },
            current: () => {
                this._updateState(); 
            },
            initialstate: () => {
                this._updateState();
            },
            completestate: () => {
                this._updateState();
            },
            label: () => {
                this._updateState();
            }
        };
    }

    _createStepElements(){
        const fragment = document.createDocumentFragment();

        this.steps.forEach(() => {
            const stepElement = this.dom.steptemplate.content.firstElementChild.cloneNode(true);

            fragment.appendChild(stepElement);
        });

        this.dom.stepelements.appendChild(fragment);
    }

    get #stepBounds () {
        return {
            min: 0,
            // Max value is +1 so all steps can show as complete
            max: this.steps.length + 1
        };
    }

    get #current () {
        // Having a private current allows the component to support intialState and CompleteState
        return this.current - 1;
    }

    _updateState () {
        if (!this.steps) {
            return;
        }

        this.current = wealthify.type.number.getClampedValue(this.current, this.#stepBounds.min, this.#stepBounds.max);

        const currentStepClass = `${this.styleSelector}__step--current`;
        const completeStepClass = `${this.styleSelector}__step--complete`;

        let {
            label: stepLabel, 
            description: stepDescription
        } = this.current === this.#stepBounds.max
            ? this.completestate
            : this.initialstate;

        let ariaLabel = this.current === this.#stepBounds.max 
            ? `Progress of ${this.label} is complete`
            : `Progress of ${this.label} has not started`;

        this.dom.stepelements.childNodes.forEach((step, index) => {
            if (index < this.#current) {
                // Set step to complete
                step.classList.remove(currentStepClass);
                step.classList.add(completeStepClass);
            } else if (index === this.#current) {
                // Set step to current
                step.classList.remove(completeStepClass);
                step.classList.add(currentStepClass);
                
                stepLabel = this.steps[this.#current].label;
                stepDescription = this.steps[this.#current].description;
                ariaLabel = `Progress of ${this.label} at step ${this.current} of ${this.steps.length}`;
            } else {
                // Set step to default
                step.classList.remove(completeStepClass);
                step.classList.remove(currentStepClass);
            }
        });

        this.dom.arialabel.textContent = ariaLabel ;
        this.dom.label.textContent = stepLabel;
        this.dom.description.textContent = stepDescription;
    }
});